import { type ReactElement } from 'react';
import { r, s, typographyTokens } from '@/theme/tokens';
import {
  Box,
  CursorTooltip,
  Text,
  getStatusChipColor,
  resolveStatusChipLabel,
} from '@/ui/primitives';
import {
  BlockOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  ErrorOutline,
  HelpIcon,
} from '@/ui/primitives/icons';
import {
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayMetaFontSize,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
} from '@/ui/primitives/tooltipOverlayStyles';
import { metricsStatusOrder } from '@flowqa/shared';
import { resolveTableMetricAccentColor } from './tableMetricAccentStyles';
import { formatTableV2PassRate, formatTableV2StatusPercent } from './TableV2PassRateCell.helpers';
import type { TableV2Counts } from './TableV2PassRateCell.helpers';
import type { SvgIconComponent, Theme } from '@/ui/primitives';
import type { ExecutionStatusKey } from '@flowqa/shared';

type TableV2PassRateTooltipRow = {
  key: ExecutionStatusKey;
  value: number;
};

const PASS_RATE_TOOLTIP_STATUS_ICON_SIZE = '14px';
const PASS_RATE_TOOLTIP_ROW_PADDING_Y = '2px';

const passRateTooltipTextSx = {
  fontSize: tooltipOverlayFontSize,
  fontWeight: tooltipOverlayBodyFontWeight,
  lineHeight: tooltipOverlayCompactLineHeight,
} as const;

const resolveTableV2PassRateStatusColor = (theme: Theme, status: ExecutionStatusKey): string => {
  const statusTone = getStatusChipColor(status);
  return resolveTableMetricAccentColor(theme, statusTone);
};

const resolveStatusIcon = (status: ExecutionStatusKey): SvgIconComponent => {
  if (status === 'passed') return CheckCircleOutlineIcon;
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'errored') return ErrorOutline;
  if (status === 'skipped') return BlockOutlinedIcon;
  return HelpIcon;
};

const buildTooltipRows = (counts: TableV2Counts): TableV2PassRateTooltipRow[] =>
  metricsStatusOrder
    .map((statusKey) => ({
      key: statusKey,
      value: counts[statusKey],
    }))
    .filter((row) => row.value > 0);

const TableV2PassRateTooltipContent = ({
  rows,
  total,
}: {
  rows: TableV2PassRateTooltipRow[];
  total: number;
}): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      rowGap: s('xxs'),
      minWidth: '168px',
    }}
  >
    {rows.map((row) => {
      const StatusIcon = resolveStatusIcon(row.key);
      return (
        <Box
          key={row.key}
          sx={{
            alignItems: 'center',
            display: 'grid',
            gridTemplateColumns: `${PASS_RATE_TOOLTIP_STATUS_ICON_SIZE} ${s('sm')} minmax(0, 1fr) ${s('xs')} max-content max-content`,
            py: PASS_RATE_TOOLTIP_ROW_PADDING_Y,
          }}
        >
          <StatusIcon
            aria-hidden="true"
            fontSize="small"
            sx={{
              color: (theme) => resolveTableV2PassRateStatusColor(theme, row.key),
              flex: '0 0 auto',
              fontSize: PASS_RATE_TOOLTIP_STATUS_ICON_SIZE,
              height: PASS_RATE_TOOLTIP_STATUS_ICON_SIZE,
              width: PASS_RATE_TOOLTIP_STATUS_ICON_SIZE,
            }}
          />
          <Box component="span" aria-hidden="true" />
          <Text
            role="body"
            component="span"
            color="inherit"
            sx={[
              passRateTooltipTextSx,
              {
                fontWeight: tooltipOverlayLabelFontWeight,
              },
            ]}
          >
            {resolveStatusChipLabel(row.key)}
          </Text>
          <Box component="span" aria-hidden="true" />
          <Text
            role="body"
            component="span"
            color="inherit"
            sx={[
              passRateTooltipTextSx,
              {
                fontVariantNumeric: 'tabular-nums',
                fontWeight: tooltipOverlayMetricFontWeight,
                minWidth: '3ch',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              },
            ]}
          >
            {row.value.toLocaleString()}
          </Text>
          <Text
            role="body"
            component="span"
            color="inherit"
            sx={[
              passRateTooltipTextSx,
              {
                fontVariantNumeric: 'tabular-nums',
                color: tooltipOverlayTextColor,
                fontSize: tooltipOverlayMetaFontSize,
                ml: s('md'),
                minWidth: '6ch',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              },
            ]}
          >
            ({formatTableV2StatusPercent(row.value, total)})
          </Text>
        </Box>
      );
    })}
  </Box>
);

const passRateTooltipSlotProps = {
  tooltip: {
    sx: {
      px: tooltipOverlayPaddingX,
      py: tooltipOverlayPaddingY,
      maxWidth: '260px',
      width: 'fit-content',
    },
  },
} as const;

export const TableV2PassRateStatusBar = ({ counts }: { counts: TableV2Counts }): ReactElement => {
  const hasCounts = counts.total > 0;
  const segments = buildTooltipRows(counts);

  return (
    <CursorTooltip
      title={<TableV2PassRateTooltipContent rows={segments} total={counts.total} />}
      disableHoverListener={!hasCounts || segments.length === 0}
      arrow
      slotProps={passRateTooltipSlotProps}
    >
      <Box
        aria-label="Status breakdown"
        role="img"
        sx={{
          backgroundColor: (theme) => theme.app.state.disabledBg,
          borderRadius: r('sm'),
          display: 'flex',
          height: s('sm'),
          mt: s('sm'),
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {segments.map((segment) => (
          <Box
            key={segment.key}
            sx={{
              backgroundColor: (theme) => resolveTableV2PassRateStatusColor(theme, segment.key),
              height: '100%',
              width: hasCounts ? `${String((segment.value / counts.total) * 100)}%` : '0%',
            }}
          />
        ))}
      </Box>
    </CursorTooltip>
  );
};

export const TableV2PassRateCell = ({ counts }: { counts: TableV2Counts }): ReactElement => {
  if (counts.total <= 0) {
    return (
      <Text role="body" color="textSecondary" component="span">
        -
      </Text>
    );
  }

  return (
    <Box sx={{ minWidth: s('3xl') }}>
      <Box sx={{ alignItems: 'baseline', display: 'flex', gap: s('sm') }}>
        <Text role="body" component="span" sx={{ fontWeight: typographyTokens.body.fontWeight }}>
          {formatTableV2PassRate(counts)}
        </Text>
        <Text role="captionSmall" color="textSecondary" component="span">
          ({String(counts.passed)}/{String(counts.total)})
        </Text>
      </Box>
      <TableV2PassRateStatusBar counts={counts} />
    </Box>
  );
};
